import {useCallback, useEffect, useRef, useState} from 'react';

import styled from 'styled-components';

import VerificationForm from '~/shared/components/VerificationForm';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/media';
import {getLocalizationService} from '~/shared/services/localisationService';
import {headingXS, subheadingSBold} from '~/shared/theme/typography';
import {PieCloseModalButton} from '~/shared/components/Modals/styled';
import {flipOnLTR} from '~/shared/theme/utils';
import {attachModal} from '~/shared/components/ReduxModal';
import usePhoneVerification from '~/shared/hooks/usePhoneVerification';

const Root = styled(FlexColumn)`
  position: relative;
  height: 100%;
  ${media.minLargeMobile`
    height: auto;
    max-width: 500px;
  `}

  &&& {
    background-color: ${({theme}) => theme.colors.surface};
  }
`;

const Header = styled.h3`
  ${subheadingSBold}
  text-align: center;
  padding: 16px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  ${media.minLargeMobile`
    ${headingXS}
  `}
`;

const Content = styled(FlexColumn)`
  padding: 32px 0;
`;

const StyledPieCloseModalButton = styled(PieCloseModalButton)`
  top: 16px;
  ${flipOnLTR`
    left: 16px;
  `}
`;

type PhoneVerificationModalProps = {
  closeModal: () => void;
  args: {
    email: string;
    getVerificationCode: (email: string) => Promise<void>;
    submitVerification: (email: string, authenticationCode?: string) => Promise<string | null>;
    onSuccess?: () => void;
  };
};

const PhoneVerificationModal = ({closeModal, args}: PhoneVerificationModalProps) => {
  const initialRequestSentRef = useRef(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const {email, onSuccess} = args;

  const {getVerificationCode, submitVerification} = usePhoneVerification();

  const {t} = getLocalizationService();

  const getVerificationCodeHandler = useCallback(async () => {
    await getVerificationCode(email);
  }, [getVerificationCode, email]);

  const submitHandler = useCallback(async ({authenticationCode}: {authenticationCode?: string}) => {
    try {
      await submitVerification(email, authenticationCode);
      if (onSuccess) {
        onSuccess();
      }
      closeModal();
    } catch (err) {
      if (err instanceof Error) {
        setVerificationError(err?.message);
      }
      throw err;
    }
  }, [submitVerification, email, closeModal, onSuccess]);

  useEffect(() => {
    if (!initialRequestSentRef.current) {
      initialRequestSentRef.current = true;
      getVerificationCodeHandler();
    }
  }, [getVerificationCodeHandler]);

  return (
    <Root>
      <StyledPieCloseModalButton onClick={closeModal} />
      <Header id="modal-title">{t('code_n_go')}</Header>
      <Content>
        <VerificationForm
          email={email}
          onReSendCode={getVerificationCodeHandler}
          onSubmit={submitHandler}
          headerText={t('to_view_your_card_details_you_need_to_verify_first')}
          resErrors={verificationError}
          setResErrors={setVerificationError}
        />
      </Content>
    </Root>
  );
};

PhoneVerificationModal.hideCloseButton = true;
PhoneVerificationModal.modalType = 'autoWidth';
attachModal('phoneVerificationModal', PhoneVerificationModal);

export default PhoneVerificationModal;
